import React, { useState } from 'react';
import { CButton, CFormInput, CFormLabel, CFormCheck } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilCloudDownload, cilTrash } from '@coreui/icons';

// File imports
import { Language } from '../../../../language/English';
import StringUtils from '../../../../services/StringUtils/StringUtils';
import { UploadPreviewImages } from '../../Components';
import { DocumentTemplateTranslationService } from '../../Api/DocumentTemplateTranslationService';
import Notification from '../../../../services/Notification/Notification';
import pdfFileIcon from '../../../../assets/images/pdf_file_icon.webp';

interface DocumentTemplateOnlyPageProps {
    formData: any;
    setFormData: any;
    setNote: (status: boolean) => void;
    setLoader: (status: boolean) => void;
    setFolder: (name: string) => void;
    stringUtils: StringUtils;
    folderName: string | null;
    previewImageNote: boolean;
}

const DocumentTemplateOnlyPage: React.FC<DocumentTemplateOnlyPageProps> = ({
    formData,
    setFormData,
    setNote,
    setLoader,
    setFolder,
    stringUtils,
    folderName,
    previewImageNote,
}) => {
    const documentTemplateService = new DocumentTemplateTranslationService();
    const notification = new Notification();
    const [pdfError, setPdfError] = useState<string>('');

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setPdfError('');
            setFormData({
                ...formData,
                documentTemplatePdfContent: e.target.files[0],
            });
        }
    };

    const handleDownload = async () => {
        try {
            setLoader(true);
            await documentTemplateService.downloadPdf(
                formData.documentTemplateTranslationId,
                `${formData.documentTitle}.pdf`,
            );
        } catch (error) {
            notification.error(Language.SOMETHING_IS_WRONG);
        } finally {
            setLoader(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(Language.PDF_DELETE_CONFIRMATION || 'Are you sure you want to delete this PDF?')) {
            try {
                setLoader(true);
                const { success, message } = await documentTemplateService.deletePdf(
                    formData.documentTemplateTranslationId,
                );
                if (success) {
                    notification.success(message || Language.DELETED_SUCCESSFULLY);
                    setPdfError('');
                    setFormData({
                        ...formData,
                        hasDocumentTemplatePdf: false,
                        showPdfTemplateDownloadOption: false,
                    });
                } else {
                    notification.error(message || Language.SOMETHING_IS_WRONG);
                }
            } catch (error) {
                notification.error(Language.SOMETHING_IS_WRONG);
            } finally {
                setLoader(false);
            }
        }
    };

    const PDFIcon = () => (
        <img src={pdfFileIcon} alt="PDF Icon" style={{ width: '100px', height: '100px', objectFit: 'contain' }} />
    );

    return (
        <div className="mb-4">
            <CFormLabel htmlFor="documentTemplatePdfContent">
                {Language.UPLOAD_DOCUMENT_TEMPLATE_PDF}
            </CFormLabel>
            <CFormInput
                type="file"
                id="documentTemplatePdfContent"
                accept=".pdf"
                onChange={handleFileChange}
                invalid={!!pdfError}
                feedback={pdfError}
                className="mb-4 shadow-sm"
            />

            {!!formData.hasDocumentTemplatePdf && (
                <div 
                    className="mb-4 p-4 border rounded shadow bg-white d-flex flex-column align-items-center justify-content-center"
                    style={{ 
                        width: 'fit-content', 
                        minWidth: '220px',
                        borderRadius: '16px',
                        background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                        boxShadow: '20px 20px 60px #d9d9d9, -20px -20px 60px #ffffff'
                    }}
                >
                    <div className="mb-3">
                        <PDFIcon />
                    </div>
                    
                    <div className="text-center mb-3">
                        <div className="fw-bold text-dark" style={{ fontSize: '1rem', letterSpacing: '0.5px' }}>
                            {formData.documentTitle || 'TEMPLATE'}
                        </div>
                    </div>
                    
                    <div className="d-flex gap-4 mt-2 pt-3 border-top w-100 justify-content-center">
                        <CButton
                            color="info"
                            variant="ghost"
                            className="p-3 shadow-sm bg-white"
                            onClick={handleDownload}
                            title={Language.PDF_DOWNLOAD || 'Download PDF'}
                            style={{ 
                                borderRadius: '12px',
                                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' 
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-5px)';
                                e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <CIcon icon={cilCloudDownload} size="xl" className="text-info" />
                        </CButton>
                        <CButton
                            color="danger"
                            variant="ghost"
                            className="p-3 shadow-sm bg-white"
                            onClick={handleDelete}
                            title={Language.PDF_DELETE || 'Delete PDF'}
                            style={{ 
                                borderRadius: '12px',
                                transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' 
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-5px)';
                                e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <CIcon icon={cilTrash} size="xl" className="text-danger" />
                        </CButton>
                    </div>
                </div>
            )}

            <div className="d-flex align-items-center mt-3 mb-4">
                <CFormCheck
                    id="showPdfTemplateDownloadOption"
                    checked={formData.showPdfTemplateDownloadOption || false}
                    label={Language.SHOW_PDF_TEMPLATE_DOWNLOAD_OPTION}
                    onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                            const hasUploadedPdf = !!formData.documentTemplatePdfContent || !!formData.hasDocumentTemplatePdf;
                            if (!hasUploadedPdf) {
                                setPdfError(Language.PDF_REQUIRED_FOR_DOWNLOAD_OPTION || 'Please upload a PDF template file first to enable this option.');
                                return;
                            }
                        }
                        setPdfError('');
                        setFormData({
                            ...formData,
                            showPdfTemplateDownloadOption: checked,
                        });
                    }}
                />
            </div>

            <UploadPreviewImages
                folderName={folderName}
                previewImageNoteState={previewImageNote}
                setPreviewImageNoteState={(status: boolean) => {
                    setNote(status);
                }}
                titleHtmlFor={stringUtils.convertToCamelCase(
                    Language.DOWNLOAD_DOCUMENT_TEMPLATE_ONLY_SETTINGS,
                )}
                setLoader={(status: boolean) => setLoader(status)}
                setUniqueFolderName={(name: string) => setFolder(name)}
                isTemplate={true}
            />
        </div>
    );
};

export default DocumentTemplateOnlyPage;
